import React, {
  useEffect, useState, useRef, Fragment,
} from 'react';
import { useTranslation } from 'react-i18next';
import cloneDeep from 'lodash.clonedeep';
import { FieldUpdateHandlerParam } from 'shared/form';
import { FormLayoutContent, FormLayoutFooter, FormLayout } from 'components/FormLayout';
import FormButtons from 'components/FormButtons';
import FormPanel from 'components/FormPanel';
import { RootState } from 'data/root.reducers';
import { FormTemplateOption, FormTemplateSection, FormTemplate } from 'data/template/template.types';
import { getSelectedDeliveryFunctionId } from 'global/global-selectors';
import * as afWhitelistSelectors from 'data/afWhitelist/afWhitelist.selectors';
import * as afWhitelistActions from 'data/afWhitelist/afWhitelist.actions';
import { ConnectedProps, connect } from 'react-redux';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import * as discoverySelectors from 'xcipio/discovery-xcipio-selectors';
import * as distributorSelectors from 'data/distributor/distributor.selectors';
import * as distributorActions from 'data/distributor/distributor.actions';
import * as authenticationSelectors from 'data/authentication/authentication.selectors';
import * as supportedFeaturesSelectors from 'data/supportedFeatures/supportedFeatures.selectors';
import { Distributor, DistributorData } from 'data/distributor/distributor.types';
import AfWhitelistForm from 'routes/AfWhitelist/components/AfWhitelistForm';
import { AdaptedAfWhitelist, AfWhitelist } from 'data/afWhitelist/afWhitelist.types';
import { afWhitelistTemplate } from '../../templates';
import {
  adaptTemplateVisibilityMode,
  adaptTemplateOptions,
  adaptAfWhitelistData,
  setOptionForTemplateSectionField,
  getAfTagsToProtocolMapKey,
  getAfProtocolOptions
} from '../../utils/afWhitelistTemplate.adapter';
import { getAuditDetailsToSubmit } from '../../utils/afWhitelistTemplateDataToSubmit';

type Ref = {
  isValid: (forceValidation: boolean, forceTouched: boolean) => boolean
  getValues: () => AdaptedAfWhitelist
}

type RouterParams = {
  history: string
}
const mapState = (state: RootState) => ({
  afWhitelistTemplateDef: adaptTemplateVisibilityMode(afWhitelistTemplate, 'edit'),
  selectedAfWhitelist: afWhitelistSelectors
    .getSelectedAfWhitelist(state)[0],
  userId: authenticationSelectors.getAuthenticationUserId(state),
  userName: authenticationSelectors.getAuthenticationUserName(state),
  isAuditEnabled: discoverySelectors.isDiscoverySettingAuditEnabled(state),
  originalSelectedAfWhitelist: afWhitelistSelectors
    .getSelectedAfWhitelist(state)[0],
  distributors: distributorSelectors.getDistributors(state),
  afWhitelist: afWhitelistSelectors.getAfWhitelist(state),
  availableAccessFunction: supportedFeaturesSelectors.getAvailableAccessFunction(state),
  enabledAccessFunction: supportedFeaturesSelectors.getEnabledAccessFunction(state),
  deliveryFunctionId: getSelectedDeliveryFunctionId(state),
});

const mapDispatch = {
  getDistributors: distributorActions.getDistributors,
  goToAfWhitelistList: afWhitelistActions.goToAfWhitelistList,
  updateAfWhitelist: afWhitelistActions.updateAfWhitelist,
  getAfWhitelist: afWhitelistActions.getAfWhitelist,
};
const connector = connect(mapState, mapDispatch);

type PropsFromRedux = ConnectedProps<typeof connector>
type PropsFromRouter = RouteComponentProps<RouterParams>

export type Props = PropsFromRedux & PropsFromRouter;

export const AfWhitelistEdit = ({
  history,
  selectedAfWhitelist,
  originalSelectedAfWhitelist,
  userId,
  userName,
  isAuditEnabled,
  afWhitelistTemplateDef,
  afWhitelist,
  distributors,
  enabledAccessFunction,
  availableAccessFunction,
  deliveryFunctionId,
  goToAfWhitelistList,
  updateAfWhitelist,
  getAfWhitelist,
  getDistributors,
}: Props): JSX.Element => {

  const formRef = useRef<Ref>(null);
  const [formData, setFormData] = useState<Partial<AdaptedAfWhitelist>>({});
  const [template, setTemplate] = useState(afWhitelistTemplateDef);
  const { t } = useTranslation();

  const id = selectedAfWhitelist?.id;

  useEffect(() => {
    if (id) {
      getAfWhitelist(String(id));
      getDistributors(deliveryFunctionId);
    }
  }, [id, getAfWhitelist, deliveryFunctionId, getDistributors]);

  useEffect(() => {
    if (afWhitelist != null && template != null && distributors != null && (!id || (id && afWhitelist.id === id))) {
      const clonedAfWhitelist = cloneDeep(afWhitelist);
      let clonedTemplate: FormTemplate | undefined = cloneDeep(template);
      clonedTemplate.formId = Date.now().toString();
      const adaptedAfWhitelist = adaptAfWhitelistData(clonedAfWhitelist, distributors);

      clonedTemplate = adaptTemplateOptions(clonedTemplate, distributors, enabledAccessFunction, availableAccessFunction, adaptedAfWhitelist);
      setTemplate(clonedTemplate);
      setFormData(adaptedAfWhitelist);
    }
  }, [afWhitelist, id, distributors, enabledAccessFunction]);

  const getCreateAfWhitelistActionPayload = () => {
    if (formRef.current === undefined || formRef.current === null) return null;

    if (!formRef.current.isValid(true, true)) {
      return null;
    }

    const afWhitelistFormData = formRef.current.getValues();
    const afWhitelistSubmitData : AfWhitelist = {
      id: '',
      AFID: afWhitelistFormData.AFID,
      AFTYPE: afWhitelistFormData.AFTYPE,
      AFPROTOCOL: afWhitelistFormData.AFPROTOCOL,
      IPADDR: afWhitelistFormData.IPADDR,
      GRAYITEM: afWhitelistFormData.GRAYITEM ? 'Y' : 'N'
    }

    const payload = {
      data: afWhitelistSubmitData,
      auditDetails: getAuditDetailsToSubmit(
        afWhitelistFormData,
        null,
        isAuditEnabled,
        userId,
        userName,
        'CREATE',
      ),
    };

    return payload;
  };

  const handleClickSave = () => {
    const payload = getCreateAfWhitelistActionPayload();
    if (payload && afWhitelist != null) {
      updateAfWhitelist(String(afWhitelist?.id), payload, history);
    }
  };

  const handleCancel = () => goToAfWhitelistList(history);

  const handleFieldUpdate = ({ name: fieldName, value }: FieldUpdateHandlerParam) => {
    if (template == null || formData == null) {
      return;
    }

    const clonedFormData = cloneDeep(formData as AdaptedAfWhitelist);
    const clonedTemplate = cloneDeep(template);
    clonedTemplate.formId = Date.now().toString();
    if (fieldName === 'AFTYPE') {
      const protocolOptions = getAfProtocolOptions(distributors, value);
      setOptionForTemplateSectionField(clonedTemplate.main as FormTemplateSection, protocolOptions, 'AFPROTOCOL');
      (clonedTemplate.main as FormTemplateSection).fields.AFPROTOCOL.initial = 'ANY';
      clonedFormData.AFPROTOCOL = 'ANY';
    }

    const newData = {
      ...clonedFormData,
      [fieldName]: value,
    };

    setFormData(newData);
    setTemplate(clonedTemplate);
  };

  const renderForm = () => {
    if (formData && template) {
      return (
        <AfWhitelistForm
          ref={formRef}
          data={formData}
          template={template}
          isEditing
          onFieldUpdate={handleFieldUpdate}
        />
      );
    }
  };
  const renderFormPanel = (formTitle: string) => {
    return (
      <FormPanel title={formTitle}>
        <FormButtons
          isEditing
          onClickCancel={handleCancel}
          onClickSave={handleClickSave}
          onClickClose={handleCancel}
        />
      </FormPanel>
    );
  };

  if (afWhitelist == null) {
    return <Fragment />;
  }

  return (
    <div data-testid="AfWhitelistEdit">
      <FormLayout>
        <FormLayoutContent>
          {renderFormPanel(t('afWhitelist.edit.title'))}
          {renderForm()}
        </FormLayoutContent>
        <FormLayoutFooter>
          {renderFormPanel('')}
        </FormLayoutFooter>
      </FormLayout>
    </div>
  );
};

export default withRouter(connector(AfWhitelistEdit));
